import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { RefreshCw, TrendingUp, TrendingDown, X, ArrowRight } from "lucide-react";

export default function RatesWidget() {
  const { currency } = useCurrency();
  const [rates, setRates] = useState(null);
  const [date, setDate] = useState("");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(100);
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("TRY");
  const [loading, setLoading] = useState(false);
  const mounted = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rates");
      setRates(data.rates);
      setDate(data.date || "");
    } catch {}
    setLoading(false);
  };

  useEffect(() => { if (!mounted.current) { mounted.current = true; load(); } }, []);

  if (!rates) return null;

  const convert = () => {
    const inTry = amount * (rates[from] || 1);
    const out = inTry / (rates[to] || 1);
    return out;
  };

  return (
    <div className="relative">
      <button
        data-testid="rates-widget"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#29B7E3] transition text-xs font-mono-num"
      >
        <div className="flex items-center gap-1">
          <span className="text-slate-500 font-sans font-bold">USD/TRY</span>
          <span className="font-bold text-[#1E2A9C]">{rates.USD?.toFixed(2)}</span>
        </div>
        <div className="w-px h-4 bg-slate-300" />
        <div className="flex items-center gap-1">
          <span className="text-slate-500 font-sans font-bold">EUR/TRY</span>
          <span className="font-bold text-[#1E2A9C]">{rates.EUR?.toFixed(2)}</span>
        </div>
        <RefreshCw className={`w-3 h-3 text-slate-400 ${loading ? "animate-spin" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl border border-slate-200 shadow-xl p-5 z-30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">TCMB Kurları</div>
              <div className="font-display font-extrabold text-slate-900">Kur Çevirici</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            {["TRY", "USD", "EUR"].map(c => (
              <div key={c} className="p-2 rounded-lg bg-slate-50 text-center">
                <div className="text-[10px] uppercase text-slate-500 font-bold">{c}/TRY</div>
                <div className="font-mono-num font-bold text-[#1E2A9C]">{rates[c]?.toFixed(4)}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase text-slate-500 font-bold">Miktar</label>
                <input data-testid="conv-amount" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-full px-3 py-2 border rounded-lg font-mono-num text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase text-slate-500 font-bold">Kaynak</label>
                <select data-testid="conv-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-center py-1"><ArrowRight className="w-4 h-4 text-slate-400" /></div>
            <div>
              <label className="text-[10px] uppercase text-slate-500 font-bold">Hedef</label>
              <select data-testid="conv-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="mt-2 p-3 rounded-lg bg-gradient-to-br from-[#1E2A9C] to-[#29B7E3] text-white text-center">
              <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Sonuç</div>
              <div data-testid="conv-result" className="font-mono-num text-2xl font-extrabold">{convert().toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} {to}</div>
            </div>
            <div className="text-[10px] text-slate-400 text-center mt-2">Kaynak: TCMB · {date}</div>
          </div>
        </div>
      )}
    </div>
  );
}
